import React, { useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Table, Popconfirm, Tooltip } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';

import styles from '../styles';
import { errorNotification, successNotification } from '../Snackbar';
import { sorter, onFilter, filterDropdown, filterIcon, get } from '../utils';
import Api from '../Api';
import constants from '../constants';
import Breadcrumbs from '../Components/Breadcrumbs';
import { getCostSheets, clearCostSheets } from '../Actions';

const CostSheetList = props => {
  const { classes } = props;
  const dispatch = useDispatch();

  const auth = useSelector(state => state.auth);
  const permissions = useMemo(() => get(auth, ['permissions'], []), [auth]);
  const costSheets = useSelector(state => state.costSheets);

  useEffect(() => {
    dispatch(getCostSheets());
    return () => {
      dispatch(clearCostSheets());
    };
  }, []);

  const onDelete = useCallback(async id => {
    try {
      await Api.delete(`/cost-sheets/${id}`);
      successNotification('Cost sheet deleted');
      dispatch(getCostSheets());
    } catch (err) {
      errorNotification(err);
    }
  }, []);

  const updateColumn = useMemo(
    () => record =>
      (
        <>
          {permissions.includes(constants.permissions.updateCostSheet) ? (
            <Tooltip placement="bottom" title="Update">
              <Link to={`/costsheet/update/${record.costSheetId}`}>
                <EditOutlined className={classes.tableIcon} />
              </Link>
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const deleteColumn = useMemo(
    () => record =>
      (
        <>
          {permissions.includes(constants.permissions.deleteCostSheet) ? (
            <Tooltip placement="bottom" title="Delete">
              <Popconfirm
                title="Are you sure you want to delete?"
                onConfirm={() => onDelete(record.costSheetId)}
                okText="Yes"
                cancelText="No"
              >
                <DeleteOutlined className={classes.tableIcon} />
              </Popconfirm>
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const columns = useMemo(() => {
    const column = [
      {
        title: 'Customer',
        dataIndex: 'customer',
        sorter: sorter('customer'),
        onFilter: onFilter('customer'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Product',
        dataIndex: 'product',
        sorter: sorter('product'),
        onFilter: onFilter('product'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Description',
        dataIndex: 'description',
        sorter: sorter('description'),
        onFilter: onFilter('description'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Total',
        children: [
          {
            title: 'A',
            dataIndex: 'totalA',
            sorter: sorter('totalA'),
            onFilter: onFilter('totalA'),
            filterDropdown,
            filterIcon,
            render: (_text, record) =>
              record.currency
                ? `${record.currency} ${record.totalA}`
                : `INR ${record.totalA}`,
          },
          {
            title: 'B',
            dataIndex: 'totalB',
            sorter: sorter('totalB'),
            onFilter: onFilter('totalB'),
            filterDropdown,
            filterIcon,
            render: (_text, record) =>
              record.currency
                ? `${record.currency} ${record.totalB}`
                : `INR ${record.totalB}`,
          },
          {
            title: 'C',
            dataIndex: 'totalC',
            sorter: sorter('totalC'),
            onFilter: onFilter('totalC'),
            filterDropdown,
            filterIcon,
            render: (_text, record) =>
              record.currency
                ? `${record.currency} ${record.totalC}`
                : `INR ${record.totalC}`,
          },
        ],
      },
      {
        title: 'Remark',
        dataIndex: 'remark',
        sorter: sorter('remark'),
        onFilter: onFilter('remark'),
        filterDropdown,
        filterIcon,
      },
    ];

    if (permissions.includes(constants.permissions.updateCostSheet)) {
      column.push({
        title: 'Update',
        render: updateColumn,
      });
    }

    if (permissions.includes(constants.permissions.deleteCostSheet)) {
      column.push({
        title: 'Delete',
        render: deleteColumn,
      });
    }

    return column;
  }, []);

  const rowKey = useMemo(() => record => record.id, []);

  const pagination = useMemo(
    () => ({
      defaultPageSize: 20,
      position: ['bottomLeft'],
      showTotal: total => `Total ${total} Records`,
    }),
    []
  );

  const scroll = useMemo(() => ({ x: 1200, y: window.innerHeight - 300 }), []);

  return (
    <>
      <Breadcrumbs
        moduleName="Cost Sheet"
        moduleLink="/costsheet/list"
        subModuleName="List"
      />
      <div className={classes.tableStyle}>
        <Table
          bordered
          size="small"
          columns={columns}
          dataSource={costSheets}
          rowKey={rowKey}
          pagination={pagination}
          scroll={scroll}
        />
      </div>
    </>
  );
};

CostSheetList.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(CostSheetList);
